import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import cn from 'classnames';
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';

import { LandingApi } from 'src/api';
import { CONTACT_EMAIL } from 'src/constants/misc';
import { genLeadId, pushDataLayerEvent } from 'src/utils/helpers';

import Button from '../Button';
import { DoneIcon } from '../Icons';
import Input from '../Input';
import PhoneInput from '../PhoneInput';

import style from '../ContactForm/ContactForm.module.scss';

// A separate Zapier flow can be set up for these sign-ups; until then the contact webhook gets them
const ZAPIER_WEBHOOK_URL = import.meta.env.VITE_ZAPIER_VETERINARO_WEBHOOK_URL || import.meta.env.VITE_ZAPIER_WEBHOOK_URL;
const FORM_TYPE = 'veterinaro';
// The contact API requires a phone number, '0' means it was not given
const NO_PHONE = '0';

const EMPTY = { company: '', nip: '', email: '', phone: '', message: '', country: 'PL' };

const RULES = {
  company: (values) => values.company.trim().length >= 2,
  nip: (values) => !values.nip.trim() || values.nip.replace(/\D/g, '').length === 10,
  email: (values) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim()),
  phone: (values) => !values.phone.trim() || isValidPhoneNumber(values.phone, values.country)
};

const ERRORS = {
  company: 'joinForm.errors.company',
  nip: 'joinForm.errors.nip',
  email: 'contactForm.errors.email',
  phone: 'joinForm.errors.phone'
};

const isInvalid = (field, values) => !RULES[field](values);

function sendToZapier({ company, nip, email, phone, message }) {
  if (!ZAPIER_WEBHOOK_URL) return;
  fetch(ZAPIER_WEBHOOK_URL, {
    method: 'POST',
    mode: 'no-cors',
    body: new URLSearchParams({
      'Typ formularza': FORM_TYPE,
      'Nazwa kliniki': company,
      NIP: nip,
      Email: email,
      'Numer telefonu': phone,
      Pytanie: message,
      'Data wysłania': new Date().toISOString()
    })
  }).catch(() => {});
}

export default function JoinGroupForm({ active, onSuccess, onClose }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const formRef = useRef(null);
  const doneButtonRef = useRef(null);

  useEffect(() => {
    if (status === 'done') doneButtonRef.current.focus({ preventScroll: true });
  }, [status]);

  const setValue = (field, value) => {
    const next = { ...values, [field]: value };
    const checked = field === 'country' ? 'phone' : field;
    setValues(next);
    // Live re-check only for fields already marked as invalid
    if (errors[checked]) setErrors((prev) => ({ ...prev, [checked]: isInvalid(checked, next) }));
  };

  const fieldProps = (field) => ({
    name: field,
    value: values[field],
    onChange: (e) => setValue(field, e.target.value),
    onBlur: () => RULES[field] && values[field] && setErrors((prev) => ({ ...prev, [field]: isInvalid(field, values) })),
    error: errors[field] ? t(ERRORS[field]) : null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = Object.fromEntries(Object.keys(RULES).map((field) => [field, isInvalid(field, values)]));
    const firstInvalid = Object.keys(RULES).find((field) => nextErrors[field]);
    setErrors(nextErrors);
    if (firstInvalid) {
      formRef.current.elements.namedItem(firstInvalid).focus();
      return;
    }

    const phone = values.phone.trim() ? parsePhoneNumberFromString(values.phone, values.country).number : '';
    const nip = values.nip.replace(/\D/g, '');
    const data = { ...values, phone, nip };

    try {
      setStatus('sending');
      const leadId = genLeadId();
      await LandingApi.contactUs({
        name: data.company,
        email: data.email,
        phone_number: phone || NO_PHONE,
        company: data.company,
        body: [t('joinForm.apiSubject', { lng: 'pl' }), nip && `NIP: ${nip}`, data.message].filter(Boolean).join('\n\n')
      });
      sendToZapier(data);
      pushDataLayerEvent('form_submit', {
        form_type: FORM_TYPE,
        company: data.company,
        nip,
        email: data.email,
        phone,
        lead_id: leadId
      });
      setStatus('done');
      onSuccess();
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className={style.done}>
        <span className={style.check}>
          <DoneIcon />
        </span>
        <h3>{t('contactForm.doneTitle')}</h3>
        <p>{t('joinForm.doneText')}</p>
        <Button
          ref={doneButtonRef}
          variant='ghost'
          onClick={onClose}
        >
          {t('contactForm.close')}
        </Button>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      className={cn(active && style.in)}
      noValidate
      onSubmit={handleSubmit}
    >
      <p className={style.k}>{t('joinForm.label')}</p>
      <div className={style.grid}>
        <Input
          id='dmCompany'
          label={t('contactForm.company')}
          autoComplete='organization'
          required
          {...fieldProps('company')}
        />
        <Input
          id='dmNip'
          label={t('joinForm.nip')}
          inputMode='numeric'
          autoComplete='off'
          placeholder='123-456-78-90'
          {...fieldProps('nip')}
        />
        <Input
          id='dmEmail'
          type='email'
          label={t('contactForm.email')}
          autoComplete='email'
          required
          {...fieldProps('email')}
        />
        <PhoneInput
          id='dmPhone'
          label={t('contactForm.phone')}
          countryLabel={t('contactForm.countryCode')}
          country={values.country}
          onCountryChange={(country) => setValue('country', country)}
          {...fieldProps('phone')}
        />
        <Input
          id='dmMsg'
          as='textarea'
          rows={4}
          wide
          label={t('contactForm.message')}
          placeholder={t('joinForm.messagePlaceholder')}
          {...fieldProps('message')}
        />
      </div>
      <div className={style.foot}>
        <p className={style.legal}>
          <Trans
            i18nKey='joinForm.legal'
            components={{
              link: (
                <Link
                  to='/privacy-policy'
                  onClick={onClose}
                />
              )
            }}
          />
        </p>
        <Button
          type='submit'
          size='lg'
          arrow
          disabled={status === 'sending'}
        >
          {status === 'sending' ? t('contactForm.sending') : t('contactForm.submit')}
        </Button>
      </div>
      {status === 'error' && (
        <p
          className={style.submitError}
          role='alert'
        >
          {t('contactForm.errors.submit', { email: CONTACT_EMAIL })}
        </p>
      )}
    </form>
  );
}
